import { motion } from "framer-motion";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const faqs = [
  {
    q: "What kinds of Google reviews can actually be removed?",
    a: "Google removes reviews that violate its content policies. That includes reviews from people who never visited your business, off-topic content, hate speech, harassment, spam, conflicts of interest (competitors, ex-employees with a grudge), impersonation, and reviews that reveal personal information. Our audit tells you exactly which of your reviews qualify.",
  },
  {
    q: "What if a review can't be removed?",
    a: "You pay nothing. We only invoice after Google has confirmed the removal — verifiable directly in your Google Business Profile. If a review doesn't qualify or Google declines, there is no charge. Ever.",
  },
  {
    q: "How long does removal take?",
    a: "Most successful removals take 5-21 days. Straightforward policy violations (fake reviews, off-topic) tend to move fastest. Complex cases involving escalation can take 4-6 weeks. We keep you updated every step of the way inside your dashboard.",
  },
  {
    q: "Is this against Google's terms of service?",
    a: "No. We only use Google's own reporting workflows and escalation channels — the same tools available to any business owner, applied methodically by specialists who know exactly which policies apply. We never buy reviews, post fake positives, or use black-hat tactics.",
  },
  {
    q: "How is $195 per review calculated?",
    a: "It's a flat, transparent fee per successfully removed review — regardless of how many escalations we file. You'll always know your maximum possible cost after the free audit tells you how many reviews qualify.",
  },
  {
    q: "Will the reviewer know we removed their review?",
    a: "Google typically notifies the reviewer that their review has been removed for policy violations — but not who requested it. There's no direct line back to your business, and no comment thread is generated. It stays anonymous on your side.",
  },
  {
    q: "Can removed reviews come back?",
    a: "Occasionally the same reviewer will re-post a modified version of a removed review. If it still violates policy, we file it again at no extra charge as part of your Monitoring plan. Reviewers rarely persist beyond a second removal.",
  },
  {
    q: "Do you work with businesses outside the US?",
    a: "Yes. Google's content policies apply worldwide, and we've helped businesses across the US, Canada, UK, Australia, and Western Europe. Invoicing is in USD via card or ACH; international ACH may take a few extra business days to clear.",
  },
  {
    q: "What do you need from me to start the free audit?",
    a: "Just three things: your business name, a link to your Google Business Profile (or a search-friendly business name and city), and a short note about which reviews are hurting you. That's it — no contracts, no credit card, no calls unless you request one.",
  },
  {
    q: "What happens after I submit the free audit?",
    a: "Within one business day a reputation specialist analyzes every negative review on your profile against Google's policies. You receive a written report listing which reviews qualify for removal, an estimated timeline, and a maximum cost. No commitment — you choose whether to proceed.",
  },
  {
    q: "Can you also help improve my rating going forward?",
    a: "Yes. Our $45/month Monitoring plan alerts you the moment new negative reviews appear, and our Ranking Improvement service helps you generate legitimate reviews from happy customers through Google-compliant workflows — never gated, never incentivized.",
  },
  {
    q: "How do payments work? Do you accept ACH?",
    a: "Yes. You can pay by card or ACH bank transfer. Removal invoices are sent only after Google confirms removal. Monitoring is billed monthly. Note: ACH payments typically clear in ~4 business days, so we require ACH invoices to clear before beginning work on additional removals.",
  },
];

export default function FAQ() {
  return (
    <section id="faq" className="relative py-24 lg:py-32 border-y border-white/5">
      <div className="max-w-4xl mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center"
        >
          <div className="text-xs uppercase tracking-[0.2em] text-[#10B981] font-semibold">
            FAQ
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold mt-3 leading-tight">
            The honest answers you deserve.
          </h2>
        </motion.div>

        <div className="mt-14">
          <Accordion
            type="single"
            collapsible
            className="w-full divide-y divide-white/5 border-y border-white/5"
            data-testid="faq-accordion"
          >
            {faqs.map((f, i) => (
              <AccordionItem
                key={i}
                value={`item-${i}`}
                className="border-none"
              >
                <AccordionTrigger
                  data-testid={`faq-trigger-${i}`}
                  className="text-left font-display font-semibold text-white text-base sm:text-lg py-5 hover:no-underline hover:text-[#10B981] group"
                >
                  <span className="flex items-center gap-3">
                    <span className="w-6 h-6 shrink-0 grid place-items-center rounded-full border border-white/10 text-[10px] text-white/40 group-hover:text-[#10B981] group-hover:border-[#10B981]/40 transition-colors font-body tabular-nums">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    {f.q}
                  </span>
                </AccordionTrigger>
                <AccordionContent className="text-white/60 text-sm leading-relaxed pb-6 pr-6">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
}
